import { UseFieldArrayReturn, useFormContext, useWatch } from 'react-hook-form';
import {
    AlertSeverity,
    AssignmentPriority,
    DashboardTaskItemFields,
    DashboardTasksFormFields,
    IClientAssignment,
    IDashboardTaskItem,
    getPriorityColor,
    getNextPriority,
    IDashboardTaskForm,
} from '../../../../common/';
import {
    FormDatePicker,
    FormInputText,
    IconButton,
    Icon,
    MuiTableCell,
    useAppDispatch,
    RegexMapOptions,
} from '../../../../base';
import { dashboardActions } from '../../../../actions';
import { formatShortDurationForDisplay } from '../../../../common/utils/durationUtils';

import {
    CheckboxCell,
    FlagCell,
    StyledTableNewRow,
    DateCell,
    ActionsCell,
} from './CreateOrEditTasks.styles';
import { useTranslation } from 'react-i18next';

interface Props {
    formId: string;
    index: number;
    fieldArrayName: DashboardTasksFormFields;
    newTasks: UseFieldArrayReturn<
        IDashboardTaskForm,
        DashboardTasksFormFields.newTasks,
        'id'
    >;
    editingTasks: UseFieldArrayReturn<
        IDashboardTaskForm,
        DashboardTasksFormFields.editingTasks,
        'id'
    >;
    updateEditMode: () => void;
    onCancelEdit: () => void;
}
const CreateOrEditTask = (props: Props) => {
    const {
        formId,
        index,
        fieldArrayName,
        newTasks,
        editingTasks,
        updateEditMode,
        onCancelEdit,
    } = props;
    const { control, getValues, setValue, trigger } = useFormContext();
    const dispatch = useAppDispatch();
    const priorityFieldName: `${typeof fieldArrayName}.${number}.${DashboardTaskItemFields.priority}` = `${fieldArrayName}.${index}.${DashboardTaskItemFields.priority}`;
    const titleFieldName: `${typeof fieldArrayName}.${number}.${DashboardTaskItemFields.title}` = `${fieldArrayName}.${index}.${DashboardTaskItemFields.title}`;
    const dueDateFieldName: `${typeof fieldArrayName}.${number}.${DashboardTaskItemFields.dueDate}` = `${fieldArrayName}.${index}.${DashboardTaskItemFields.dueDate}`;
    const durationFieldName: `${typeof fieldArrayName}.${number}.${DashboardTaskItemFields.duration}` = `${fieldArrayName}.${index}.${DashboardTaskItemFields.duration}`;

    const priority = useWatch({
        control,
        name: priorityFieldName,
    });
    const { t } = useTranslation();

    const handleConfirmTaskEdit = async (
        index: number,
        fieldArrayName: DashboardTasksFormFields
    ) => {
        const isValid = await trigger(titleFieldName);
        if (!isValid) {
            return;
        }
        const fieldName: `${typeof fieldArrayName}.${number}` = `${fieldArrayName}.${index}`;
        const taskData = getValues(fieldName) as IDashboardTaskItem;

        const readableDuration = formatShortDurationForDisplay(
            taskData.duration
        );

        let formattedPriority = AssignmentPriority.LOW;
        switch (taskData.priority) {
            case AlertSeverity.INFO:
                formattedPriority = AssignmentPriority.LOW;
                break;
            case AlertSeverity.WARNING:
                formattedPriority = AssignmentPriority.MEDIUM;
                break;
            case AlertSeverity.ERROR:
                formattedPriority = AssignmentPriority.HIGH;
                break;
            default:
                formattedPriority = AssignmentPriority.LOW;
                break;
        }

        const confirmedTask: IClientAssignment = {
            id: taskData.id,
            isCompleted: taskData.completed,
            priority: formattedPriority,
            dueDate: taskData.dueDate || undefined,
            title: taskData.title,
            duration: readableDuration || undefined,
        };

        if (fieldArrayName === DashboardTasksFormFields.newTasks) {
            dispatch(
                dashboardActions.createAssignment({
                    assignment: confirmedTask,
                })
            );
            newTasks.remove(index);
        } else {
            dispatch(
                dashboardActions.updateAssignment({
                    assignment: confirmedTask,
                })
            );
            editingTasks.remove(index);
        }

        updateEditMode();
    };

    const handlePriorityChange = (
        index: number,
        fieldArrayName: DashboardTasksFormFields
    ) => {
        const fieldName: `${typeof fieldArrayName}.${number}.${DashboardTaskItemFields.priority}` = `${fieldArrayName}.${index}.${DashboardTaskItemFields.priority}`;
        const currentTasks = getValues(fieldArrayName);

        if (
            !Array.isArray(currentTasks) ||
            index < 0 ||
            index >= currentTasks.length
        ) {
            console.error(
                `Error getting ${fieldArrayName} array or invalid index`
            );
            return;
        }

        const taskToUpdate = currentTasks[index] as IDashboardTaskItem;
        const nextPriority = getNextPriority(taskToUpdate.priority);

        setValue(fieldName, nextPriority, {
            shouldDirty: true,
        });
    };

    const handleCancelEdit = (
        index: number,
        fieldArrayName: DashboardTasksFormFields
    ) => {
        const fieldArray =
            fieldArrayName === DashboardTasksFormFields.newTasks
                ? newTasks
                : editingTasks;
        fieldArray.remove(index);
        updateEditMode();
        onCancelEdit();
    };

    return (
        <StyledTableNewRow key={formId}>
            <CheckboxCell padding="checkbox">
                <IconButton
                    size="small"
                    onClick={() => handleCancelEdit(index, fieldArrayName)}
                    sx={{ padding: 0, height: '16px', width: '16px' }}
                >
                    <Icon name="CloseIcon" size={16} />
                </IconButton>
            </CheckboxCell>
            <FlagCell padding="checkbox">
                <IconButton
                    size="small"
                    onClick={() => handlePriorityChange(index, fieldArrayName)}
                    sx={{ padding: 0, height: '20px', width: '20px' }}
                >
                    <Icon
                        name="OutlinedFlagIcon"
                        size={20}
                        sx={{ color: getPriorityColor(priority) }}
                    />
                </IconButton>
            </FlagCell>
            <DateCell isEditMode={true}>
                <FormDatePicker
                    name={dueDateFieldName}
                    control={control}
                    size="small"
                />
            </DateCell>
            <MuiTableCell>
                <FormInputText
                    name={titleFieldName}
                    control={control}
                    size="medium"
                    removeBottomPadding
                    placeholder={t('dashboardPage.enterTaskDescription')}
                    rules={{
                        required: true,
                    }}
                    InputProps={{
                        sx: {
                            '& .MuiInputBase-input': {
                                fontSize: '0.8125rem',
                                paddingTop: '6px',
                                paddingBottom: '6px',
                                textAlign: 'start',
                            },
                        },
                    }}
                />
            </MuiTableCell>
            <MuiTableCell align="right">
                <FormInputText
                    name={durationFieldName}
                    control={control}
                    size="medium"
                    removeBottomPadding
                    validateExpression={RegexMapOptions.durationIndicator}
                    InputProps={{
                        sx: {
                            '& .MuiInputBase-input': {
                                fontSize: '0.8125rem',
                                paddingTop: '6px',
                                paddingBottom: '6px',
                            },
                        },
                    }}
                />
            </MuiTableCell>
            <ActionsCell padding="checkbox">
                <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleConfirmTaskEdit(index, fieldArrayName)}
                    sx={{
                        backgroundColor: 'primary.main',
                        color: 'white',
                        width: '18px',
                        height: '18px',
                        '&:hover': { backgroundColor: 'primary.dark' },
                    }}
                >
                    <Icon name="CheckRoundedIcon" size={14} />
                </IconButton>
            </ActionsCell>
        </StyledTableNewRow>
    );
};

export default CreateOrEditTask;
